import React from 'react';
import { useOrganizationClientRecordAuditLogs } from '../../../../../hooks/api/useOrganizationClientRecordAuditLogs';
import { TableConfig } from '../../../../v2BaseComponents/RichTable/types';
import { getIntl } from '../../../../../utils/intl';
import RichTable from '../../../../v2BaseComponents/RichTable';
import { useRichTableState } from '../../../../v2BaseComponents/RichTable/useRichTableState';
import { AuditLogs } from '../../../../../primary-record-types';
import getName from '../../../../../utils/getName';
import getDateString from '../../../../../utils/getDateString';

const intl = getIntl('client-activity-history-screen-table', {
    user: 'User',
    action: 'Action',
    time: 'Time',
    details: 'Details',
    resourceCreation: 'Create {resourceType}',
    resourceUpdate: 'Update {resourceType}',
    resourceDeletion: 'Delete {resourceType}',
    noteCreation: 'Create note',
    noteMove: 'Move note',
    noteComment: 'Comment on note',
    noteCommentDetails: '"{comment}" on {noteTitle}',
    portalAdd: 'Add portal',
    portalRefresh: 'Refresh portal',
    sharedReportCreation: 'Create shared report',
    sharedReportDeletion: 'Delete shared report',
    profileMessage: 'Message profile',
    fileUploadDetails: 'Upload file {name}',
    fileDeleteDetails: 'Delete file {name}',
    filesDeleteDetails: 'Delete {count} files',
    filesUploadDetails: 'Upload {count} files',
    filesUploadMessage: 'Upload files',
    fileDeleteMessage: 'Delete files',
    invoiceCreation: 'Create invoice',
    editCodeStatus: 'Edit code status',
    editPOA: 'Edit POA',
    addCodeStatus: 'Add code status',
    addPOA: 'Add POA',
});

export const potentialOrganizationUserAuditLogTypes = [
    'resource-creation',
    'resource-update',
    'resource-delete',
    'note-creation',
    'note-move',
    'note-comment',
    '1up-integration-add',
    '1up-integration-refresh',
    'shared-report-creation',
    'profile-message',
] as const;

function getSingleReference<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference>,
): AuditLogs.SingleAuditLogReferenceValue<TReference> {
    if (Array.isArray(reference)) {
        console.error('Expected single reference but got an array', reference);
        return reference[0];
    } else {
        return reference;
    }
}

function getReferenceAsArray<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference>,
): AuditLogs.ListAuditLogReferenceValue<TReference> {
    if (Array.isArray(reference)) {
        return reference;
    } else {
        return [reference];
    }
}

const getActionMap: {
    [Type in AuditLogs.PotentialOrganizationUserAuditLogType]: (
        log: AuditLogs.AuditLogMetadataWithPotentialOrganizationUserForType<Type>,
    ) => string;
} = {
    'resource-creation': (log) =>
        intl('resourceCreation', { resourceType: getSingleReference(log.references.resource).references.category }),
    'resource-delete': (log) =>
        intl('resourceDeletion', { resourceType: getSingleReference(log.references.resource).references.category }),
    'resource-update': (log) =>
        intl('resourceUpdate', { resourceType: getSingleReference(log.references.resource).references.category }),
    'note-creation': () => intl('noteCreation'),
    'note-move': () => intl('noteMove'),
    'note-comment': () => intl('noteComment'),
    '1up-integration-add': () => intl('portalAdd'),
    '1up-integration-refresh': () => intl('portalRefresh'),
    'shared-report-creation': () => intl('sharedReportCreation'),
    'shared-report-deletion': () => intl('sharedReportDeletion'),
    'profile-message': () => intl('profileMessage'),
    'medical-profile-file-upload': () => intl('filesUploadMessage'),
    'medical-profile-file-delete': () => intl('fileDeleteMessage'),
    'organization-invoice-creation': () => intl('invoiceCreation'),
    'code-status-directive-creation': () => intl('addCodeStatus'),
    'code-status-directive-update': () => intl('editCodeStatus'),
    'poa-directive-creation': () => intl('addPOA'),
    'poa-directive-update': () => intl('editPOA'),
};

const getAction = (log: AuditLogs.AuditLogMetadataWithPotentialOrganizationUser): string => {
    return getActionMap[log.type](log as any);
};

const getDetailsMap: {
    [Type in AuditLogs.PotentialOrganizationUserAuditLogType]: (
        log: AuditLogs.AuditLogMetadataWithPotentialOrganizationUserForType<Type>,
    ) => string;
} = {
    'resource-creation': (log) => getSingleReference(log.references.resource).references.description,
    'resource-delete': (log) => getSingleReference(log.references.resource).references.description,
    'resource-update': (log) => getSingleReference(log.references.resource).references.description,
    'note-creation': (log) => getSingleReference(log.references.note).references.noteTitle,
    'note-move': (log) => getSingleReference(log.references.note).references.noteTitle,
    'note-comment': (log) =>
        intl('noteCommentDetails', {
            comment: getSingleReference(log.references.noteComment).references.noteComment,
            noteTitle: getSingleReference(log.references.note).references.noteTitle,
        }),
    '1up-integration-add': (log) =>
        getSingleReference(log.references.oneUpHealthConnectedHealthSystem).references.connectedHealthSystem,
    '1up-integration-refresh': (log) =>
        getSingleReference(log.references.oneUpHealthConnectedHealthSystem).references.connectedHealthSystem,
    'shared-report-creation': (log) => getSingleReference(log.references.sharedReport).references.sharedReportType,
    'shared-report-deletion': (log) => getSingleReference(log.references.sharedReport).references.sharedReportType,
    'profile-message': (log) => log.references.message,
    'medical-profile-file-upload': (log) => {
        const referencesAsArray = getReferenceAsArray(log.references.medicalProfileFile);
        if (referencesAsArray.length === 1) {
            return intl('fileUploadDetails', { name: referencesAsArray[0].references.fileName });
        } else {
            return intl('filesUploadDetails', { count: referencesAsArray.length });
        }
    },
    'medical-profile-file-delete': (log) => {
        const referencesAsArray = getReferenceAsArray(log.references.medicalProfileFile);
        if (referencesAsArray.length === 1) {
            return intl('fileDeleteDetails', { name: referencesAsArray[0].references.fileName });
        } else {
            return intl('filesDeleteDetails', { count: referencesAsArray.length });
        }
    },
    'organization-invoice-creation': (log) =>
        getSingleReference(log.references.organizationInvoice).references.description || '',
    'code-status-directive-creation': () => '',
    'code-status-directive-update': () => '',
    'poa-directive-creation': () => '',
    'poa-directive-update': () => '',
};

const getDetails = (log: AuditLogs.AuditLogMetadataWithPotentialOrganizationUser): string => {
    return getDetailsMap[log.type](log as any);
};

const getUser: (log: AuditLogs.AuditLogMetadataWithPotentialOrganizationUser) => string = (log) =>
    getName(getSingleReference(log.references.user).references);

export type ClientActivityHistoryTableOwnProps = {
    clientRecordId: string;
};

export type ClientActivityHistoryTableProps = ClientActivityHistoryTableOwnProps;

const ClientActivityHistoryTable: React.FC<ClientActivityHistoryTableProps> = (props) => {
    const [tableState, setTableState] = useRichTableState();

    const { auditLogs, isFetchingAuditLogs } = useOrganizationClientRecordAuditLogs({
        organizationClientRecordId: props.clientRecordId,
    });

    const validatedAuditLogs = auditLogs
        .map((result) => result.auditLog)
        .filter(
            (
                log,
            ): log is AuditLogs.AuditLogMetadataWithPotentialOrganizationUser & {
                auditLogId: string;
                timestamp: number;
            } => potentialOrganizationUserAuditLogTypes.includes(log.type as any),
        );

    const auditLogsTableConfig: TableConfig<
        AuditLogs.AuditLogMetadataWithPotentialOrganizationUser & {
            timestamp: number;
            auditLogId: string;
        }
    > = {
        columns: [
            {
                label: intl('time'),
                columnKey: 'time',
                getSortValue: (item) => item.timestamp,
                getContentKey: (item) => getDateString(item.timestamp, 'date-time'),
                getSearchValue: (item) => getDateString(item.timestamp, 'date-time'),
                render: (item) => getDateString(item.timestamp, 'date-time'),
            },
            {
                label: intl('user'),
                columnKey: 'user',
                getSortValue: getUser,
                getContentKey: getUser,
                getSearchValue: getUser,
                getDiscreetValue: getUser,
                render: getUser,
            },
            {
                label: intl('action'),
                columnKey: 'action',
                getSortValue: getAction,
                getContentKey: getAction,
                getSearchValue: getAction,
                getDiscreetValue: getAction,
                render: getAction,
            },
            {
                label: intl('details'),
                columnKey: 'details',
                getSortValue: getDetails,
                getContentKey: getDetails,
                getSearchValue: getDetails,
                render: getDetails,
            },
        ],
        getRowKey: (auditLog) => auditLog.auditLogId,
    };

    return (
        <RichTable
            config={auditLogsTableConfig}
            state={tableState}
            onStateChange={setTableState}
            data={validatedAuditLogs}
            minWidth={'100%'}
            allowRowSelection={false}
            style={{ height: '100%' }}
            isFetching={isFetchingAuditLogs}
        />
    );
};

export default React.memo(ClientActivityHistoryTable);
